<script>
import constants from '@/libs/Constants';
import Pixel from "@/components/Pixel.vue";
import {flagUsePixel} from "@/libs/Helpers";
import plausible from "@/libs/PlausibleTracker";

export default {
  components: {Pixel},
  data() {
    return {
      constants,
      loginLink: constants.ROUTING.LANDING,

      //Get from environment variables
      domain: process.env.VUE_APP_PRIVACY_DOMAIN,
      siteName: process.env.VUE_APP_PRIVACY_SITE_NAME,
      privacyEmail: process.env.VUE_APP_PRIVACY_EMAIL,
      privacyEmailSubject: process.env.VUE_APP_PRIVACY_EMAIL_SUBJECT
    }
  },
  methods: {
    flagUsePixel
  },
  mounted() {
    plausible.trackPageview();
  }
}
</script>

<template>
  <main class="flex-shrink-0 m-4 p-2">
    <div class="container">
      <div class="row">
        <div class="col-12 offset-0 col-sm-10 offset-sm-1">
          <div class="row">
            <div class="col-sm-6 offset-sm-3 col-12 offset-0 text-center">
              <router-link :to="{name: loginLink}">
                <img src="@/assets/img/logos/logo.png" alt="Logo" height="80" class="d-inline-block align-top"/>
              </router-link>
            </div>
          </div>
          <h2 class="text-blue mt-4 text-center">Privacy Policy</h2>
          <p class="text-center mb-4">
            <span class="fw-semibold">Last update: </span><span class="text-muted">27/01/2025</span>
          </p>
          <p>
            In accordance with EU Regulation No. 2016/679 –General Data Protection Regulation (GDPR) as well
            as national legislation, hereinafter collectively referred to as the “Applicable Law”, this
            policy statement provides information with regard to the processing of personal data of users
            and visitors of {{ siteName }} website. These services are accessible at
            the domain <span class="fw-semibold">{{ domain }}</span>.
          </p>
          <p>
            This policy is also in conformity with Recommendation n. 2/2001 related to the minimum
            requirements for online data collection in the European Union, adopted by the European Data
            Protection Authorities - Working Party of Article 29, Directive No. 95/46/EU on 17 May 2001 and
            the Guidelines on the use of cookies and other tracking tools of 10 June 2021 published in the
            Official Journal of the Italian Republic No 163 of 9 July 2021).
          </p>
          <p>
            The policy described herein applies solely to the websites of {{ siteName }} and
            excludes all other websites that can be accessed via links that appear on the Eurac Research
            websites. Eurac Research is not responsible for the content or any external links to other
            websites.
          </p>
          <p>
            Pursuant to legal provisions, Eurac Research guarantees that the processing of personal data
            will be performed in consideration of fundamental rights and freedoms as well as the dignity of
            the data subject, and in accordance with the legislative provisions of the Applicable Law and
            the confidentiality clauses included therein. In particular, the processing of personal data
            will be carried out in accordance with the principles of lawfulness, fairness, transparency,
            accuracy, purpose and storage limitations, data minimisation, integrity and confidentiality.
          </p>
          <p>
            Before providing any personal data or completing an electronic online form, Eurac Research
            invites users to carefully read this privacy policy.
          </p>
          <p>
            The content of this privacy policy may be amended or updated to conform to legal and regulatory
            obligations with respect to data protection, to allow for technological advances on the webpage
            that could impact the modalities of processing and to reflect organisational modifications that
            affect the privacy structure.
          </p>
          <p>
            Precise data protection information about specific services or data processing procedures may be
            displayed on the corresponding webpages of this website or transmitted directly to the data
            user.
          </p>
          <h4 class="text-success">1. Data Controller and Data Protection Officer</h4>
          <p>
            Data Controller: Eurac Research, with headquarters at Viale Druso 1, 39100 Bolzano, in the
            person of the legal representative pro tempore. You can contact the DPO under the following
            e-mail address: <a
              :href="`mailto:${this.privacyEmail}?subject=${this.privacyEmailSubject}`">
            {{ this.privacyEmail }}
          </a>
          </p>
          <h4 class="text-success">2. Types of Personal Data Subject to Processing</h4>
          <p>
            “Personal data” means any information relating to an identified or identifiable natural person
            (the “Data Subject). An identifiable natural person is one who can be identified, directly or
            indirectly, in particular by reference to an identifier such as a name, an identification
            number, location data, an online identifier or to one or more factors specific to the physical,
            physiological, mental, economic, cultural or social identity of that natural person. The
            personal data that may be processed includes browsing data, data provided voluntarily by the
            data subject and cookies.
          </p>
          <h5 class="text-danger">A. Browsing Data</h5>
          <p>
            The computer systems and software used to run this website during normal operations acquire
            certain personal data whose transmission is implicit in the use of Internet communication
            protocols. This information is not collected in relationship to the users, but due to its
            inherent nature, such information could enable the users to be identified via processing and
            associations with data held by third parties. This category of data includes, for example, the
            IP addresses or domain names of the users’ computers connecting to the website.
          </p>
          <p>
            The data is used for the purposes of both obtaining statistical information concerning the
            website's use and analysing them and to monitor the website's proper operation. In certain
            cases, the data may be used to ascertain liability in a suspected computer crime directed
            against the website. Other than the aforementioned cases, data on web contacts will not be held
            beyond the time required to meet requests made of the website. Personal data will not be
            disclosed to third parties; if requested, however, such data must be made available to the
            Italian Postal and Communication Police Service, legal authorities and criminal investigation
            police.
          </p>
          <p>
            While browsing the web user’s following information will be collected and stored in the log
            files of the server (hosting) of the website:
            <ul>
              <li>Internet protocol address (IP);</li>
              <li>type of browser;</li>
              <li>Parameters of the device used to connect to the website;</li>
              <li>date and time of the visit;</li>
              <li>web page of origin of the visitor (referral) and exit;</li>
              <li>possibly the number of clicks.</li>
            </ul>
          </p>
          <h5 class="text-danger">B. Data Provided Voluntarily by Users</h5>
          <p>
            The optional, explicit and voluntary sending or transmission of e-mail and/or personal data to
            the addresses given on the website will entail the subsequent acquisition of the sender’s name
            and e-mail address (which is required to respond to requests), as well as any other personal
            data
            present in the e-mail. The provision of the information is voluntary, but refusal of such
            renders the user’s request impossible. This data will only be transmitted to third parties if
            necessary to the process of responding to the inquiry. The data will not be shared with third
            parties for marketing or profiling purposes.
          </p>
          <h5 class="text-danger">C. Cookies and Similar Technologies</h5>
          <p>
            <span class="fw-bold">I. Definition and purposes:</span> Cookies and similar technologies are
            information stored on websites and apps on the users devices during their first visit to the
            site. Cookies and related technologies allow websites and apps to remember user actions and
            preferences (such as login data, the default language, display settings, etc.) so that they will
            be available in the user’s subsequent visits. These technologies are used to perform IT
            authentications, session monitoring and to store information about the activities of users who
            access a service.
          </p>
          <p>
            In line with Applicable law, prior consent for the use of cookies is not always required. In
            particular, such consent is not required for "technical cookies", e.g., those used solely for
            the
            purpose of carrying out the transmission of a communication over an electronic communications
            network, or to the extent strictly necessary for the provider of an information society service
            explicitly requested by the contracting party or user to provide that service. In other words,
            cookies that are strictly necessary for the Website’s operation.
          </p>
          <p>
            In contrast the prior consent is required for non-anonymized analytics cookies and for profiling
            cookies, e.g., those reporting statistics on the use of a website or those creating profiles of
            users in order to send them advertising messages in line with the preferences they have
            expressed while browsing. For further information: Linee Guida Cookie 10.06.2021 – Garante
            Privacy <a href="//www.garanteprivacy.it/home/docweb/-/docweb-display/docweb/9677876"
                       target="_blank">https://www.garanteprivacy.it/home/docweb/-/docweb-display/docweb/9677876</a>.
          </p>
          <p>
            <span class="fw-bold">II. Categories of cookies used and possibility of disable them:</span>
            The website <span class="fw-semibold">{{ domain }}</span> uses “persistent technical cookies”
            (small text files that the website temporarily saves directly on the computer) that allow the
            website to remember, for example, the user’s preferred language or to show other possible
            versions of the website. For these cookies the consent is not required.
          </p>
          <p>
            The website may also use <strong>third-party cookies</strong>, which are cookies from sites or
            web servers other than this website that are used for purposes of such third parties. For
            example, “social plugins” such as Facebook, Twitter may be present on webpages and are generated
            and integrated into the host’s page by these sites. The most common use of social plugins is for
            sharing content on social networks. These plugins transmit cookies to and from all third-party
            sites. This information from “third parties” is governed by the corresponding regulations, which
            must be observed. For more information and details about the various types of cookies, their
            operation and features, see the website <a href="//www.allaboutcookies.org" target="_blank">www.allaboutcookies.org</a>.
          </p>
          <p>
            Below are links to webpages that describe the different uses of cookies:
            <ul>
              <li>
                <a href="//help.twitter.com/de/rules-and-policies/twitter-cookies"
                   target="_blank">Twitter's policy</a>
              </li>
              <li>
                <a href="//help.twitter.com/de/rules-and-policies/twitter-cookies"
                   target="_blank">Twitter's configuration</a>
              </li>
              <li>
                <a href="//support.google.com/youtube/answer/7671399?p=privacy_guidelines&hl=de&visit_id=637399370071179506-1165612239&rd=1"
                   target="_blank">Youtube policy</a>
              </li>
              <li>
                <a href="//support.google.com/youtube/topic/2803240?hl=it&ref_topic=2803240&visit_id=637399370071179506-1165612239&rd=1"
                   target="_blank">Youtube configuration</a>
              </li>
              <li>
                <a href="//www.linkedin.com/legal/cookie-policy"
                   target="_blank">LinkedIn policy</a>
              </li>
              <li>
                <a href="//www.linkedin.com/help/linkedin/answer/125463"
                   target="_blank">LinkedIn configuration</a>
              </li>
            </ul>
          </p>
          <p>
            The user may authorise, block or eliminate (in whole or in part) the use of cookies by selecting
            the relevant settings on the user’s browser. Please note that this will impede the operation of
            the website’s features.
          </p>
          <p>
            For more information on how to set your preferences on the use of cookies via your browser,
            please see the following instructions:

            <ul>
              <li>
                <a href="//privacy.microsoft.com/en-us/privacystatement"
                   target="_blank">Microsoft Edge </a>
              </li>
              <li>
                <a href="//support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer"
                   target="_blank">Firefox</a>
              </li>
              <li>
                <a href="//support.apple.com/guide/safari/privacy-sfri35610/mac"
                   target="_blank">Safari</a>
              </li>
              <li>
                <a href="//www.google.com/intl/it/chrome/privacy/"
                   target="_blank">Chrome</a>
              </li>
            </ul>
          </p>
          <h4 class="text-success">3. Purpose and Legal Basis for the Data Processing</h4>
          <p>
            The personal data provided will be processed for the following purposes:
          </p>
          <p>
            <ol>
              <li>Research or statistical analyses on aggregated or anonymous data (i.e., without
                identification of the data subject) aimed at measuring the functioning of the website,
                as well as its traffic, usability and interest;
              </li>
              <li>Completion of data collection forms for the purpose of receiving newsletters or
                communications in general via e-mail;
              </li>
              <li>The performance of a contract to which the data subject is party, or in order to process
                a data subject’s requests prior to entering into a contract;
              </li>
              <li>Compliance with a legal obligation to which the controller is subject;</li>
              <li>The establishment, exercise or defence of legal claims or whenever courts are acting in
                their judicial capacity;
              </li>
              <li>Technical and administrative purposes regarding connection set-up and stability, to
                guarantee the security and functioning of our website and to be able to track any
                illegal attacks on the website, if required.
              </li>
            </ol>
          </p>
          <p>
            The lawful basis for processing Personal Data: 1) it does not imply the processing of Personal
            Data, 2) consent, 3) performance of a contract, 4) and 5) legal obligation, 6) the legitimate
            interest concerning the security interest and the need to make our website available unhindered.
          </p>
          <h4 class="text-success">4. Mandatory or voluntary communication of data and possible consequences
            of a failure to
            provide it</h4>
          <p>
            The provision of personal data is voluntary, but refusal could interfere with the correct use of
            the services and its legal obligations, thus limiting the full functionality of the website.
          </p>
          <h4 class="text-success">5. Recipients of the Data Processed</h4>
          <p>
            The recipients of the data are the employees of Eurac Research or persons who have access to
            personal data and who are in charge of the data processing activities and authorised and
            instructed to carry out data processing activities by the data controller.
          </p>
          <p>
            Personal data may be communicated to external service providers (e.g., sending e-mails and
            analysing the functional capability of the website), which typically process Personal Data on
            behalf of Eurac Research as data processors. When required, your personal data will be forwarded
            to public administrative bodies and agencies, as provided by law. To provide certain services
            Eurac Research and the Free University of Bolzano (with legal seat in Bolzano (Italy), Piazza
            Università 1 – 39100, manage together the scientific platform Scientific Network South Tyrol
            which connects their data centers. Eurac Research and the Free University of Bolzano are Joint
            Controllers of the personal data processed in this context and, accordingly, have entered into
            an agreement pursuant to art. 26 of the GDPR. The joint controllers have agreed that user’s
            complaints and requests to exercise their rights under the GDPR will be handled within the
            framework of the processing purposes set out in the joint agreement. Anyway, data subjects may
            exercise their rights under this Regulation in respect of and against each of the controllers
            according to art. 26 par. 3 of the GDPR.
          </p>
          <p>
            Eurac Research uses Newsletter2Go – now “Sendinblue” to send our newsletter to our subscribers.
            As part of your subscription, your data will be communicated to and processed by Sendinblue
            GmbH, Köpenickerstraße 126, 10179 Berlin, in its function as Data Processor. Sendinblue is a
            service provided by Sendinblue GmbH, Germany. The data that is collected when you register for
            the newsletter (i.e., e-mail address, full name, IP address, and time and date of registration)
            will be sent to a server in the European Union and stored there in accordance with the
            requirements of the GDPR. For further information about the data protection offered by
            Newsletter2Go see: <a href="//de.sendinblue.com/legal/privacypolicy/" target="_blank">
            https://de.sendinblue.com/legal/privacypolicy/</a>.
          </p>
          <h5>Social Media</h5>
          <p>
            We are represented on various social media platforms with a company page. In this way, we would
            like to offer additional information about our research institution. Eurac Research has company
            pages on the following social media platforms: Facebook, Instagram, YouTube, Twitter, LinkedIn.
          </p>
          <p>
            When you visit or interact with a profile on a social media platform, personal data about you
            may be processed. Information associated with a regularly used social media profile also
            constitutes personal data. This includes messages and statements made while using the profile.
            In addition, when visiting a social media profile, certain information about that profile is
            often automatically collected, which may also constitute personal data. Personal data is
            processed for statistical purposes as a joint data controller pursuant to Article 26 DSGVO
            together with the operator of the social media website. For more information on data processing:
          </p>
          <p>
            <h6>Facebook und Instagram</h6>
            <ul>
              <li>Joint data controller: Facebook Ireland Limited (<a
                  target="_blank"
                  href="//www.facebook.com/legal/terms/information_about_page_insights_data">https://www.facebook.com/legal/terms/information_about_page_insights_data</a>)
              </li>
              <li>Cookies Policy Meta: <a
                  target="_blank"
                  href="//www.facebook.com/privacy/policies/cookies/?entry_point=cookie_policy_redirect&entry=0">https://www.facebook.com/privacy/policies/cookies/?entry_point=cookie_policy_redirect&entry=0</a>
              </li>
              <li>Privacy Policy Meta: <a
                  target="_blank"
                  href="//www.facebook.com/privacy/policy/?entry_point=data_policy_redirect&entry=0">https://www.facebook.com/privacy/policy/?entry_point=data_policy_redirect&entry=0</a>
              </li>
            </ul>
          </p>
          <p>
            <h6>Twitter</h6>
            <ul>
              <li>Joint data controller: Twitter International Company (<a
                  target="_blank"
                  href="//gdpr.twitter.com/en/controller-to-controller-transfers.html">https://gdpr.twitter.com/en/controller-to-controller-transfers.html</a>)
              </li>
              <li>Cookies Policy Twitter: <a
                  target="_blank"
                  href="//help.twitter.com/en/rules-and-policies/twitter-cookies">https://help.twitter.com/en/rules-and-policies/twitter-cookies</a>
              </li>
              <li>Privacy Policy Twitter: <a
                  target="_blank"
                  href="//twitter.com/en/privacy">https://twitter.com/en/privacy</a>
              </li>
            </ul>
          </p>
          <p>
            <h6>LinkedIn</h6>
            <ul>
              <li>Joint data controller: LinkedIn Ireland Limited Company (<a
                  target="_blank"
                  href="//legal.linkedin.com/pages-joint-controller-addendum">https://legal.linkedin.com/pages-joint-controller-addendum</a>)
              </li>
              <li>Cookies Policy LinkedIn: <a
                  target="_blank"
                  href="//www.linkedin.com/legal/cookie-policy">https://www.linkedin.com/legal/cookie-policy</a>
              </li>
              <li>Privacy Policy LinkedIn: <a
                  target="_blank"
                  href="//www.linkedin.com/legal/privacy-policy?trk=homepage-basic_footer-privacy-policy">https://www.linkedin.com/legal/privacy-policy?trk=homepage-basic_footer-privacy-policy</a>
              </li>
            </ul>
          </p>
          <p>
            <h6>YouTube</h6>
            <ul>
              <li>Joint data controller: Google Ireland Limited (<a
                  target="_blank"
                  href="//policies.google.com/privacy?hl=en-GB&gl=it">https://policies.google.com/privacy?hl=en-GB&gl=it</a>)
              </li>
            </ul>
          </p>
          <h4 class="text-success">6. Transfer of Data</h4>
          <p>
            The personal data collected via the services of this website are collected by employees of Eurac
            Research who have been specifically assigned this role. Alternatively, it will be processed by
            persons who carry out occasional maintenance work on the website and who have also been
            appointed for this purpose and are bound by confidentiality. To this end, Eurac Research may, in
            the context of assigning this task, and whilst adhering to the best possible security measures,
            utilise the help of external companies, consultants, associations, software suppliers and
            service providers. Some personal data could be transmitted to third countries outside of the EU
            but only if the transmission of personal data is connected to the performance of the
            institutional activities of Eurac Research. Eurac Research guarantees that in any case, the
            electronic or analogue processing of personal data by the recipient shall be in accordance with
            statutory provisions. Where data is transferred to a third country, this will be done on the
            basis of the European Commission's standard contractual clauses (SCC) with supplementary
            measures and in according with the legal requirements.
          </p>
          <h4 class="text-success">7. Retention Period of Personal Data</h4>
          <p>
            Personal data will be stored for the time necessary to carry out the purposes for which it was
            collected or as long as the service (section 3 b) is available and/or you remain subscribed to
            it. Apart from the above, your personal data will be retained for a period of time necessary or
            permitted to comply with the Applicable Law (Art. 2946 and following Italian Civil Code); when
            this period has been reached, the data shall be deleted or made anonymous.
          </p>
          <h4 class="text-success">8. Existence of automated decision-making process</h4>
          <p>
            There are no automated decision-making processes.
          </p>
          <h4 class="text-success">9. The Data Subject’s Rights</h4>
          <p>
            At any time the data subject has the right to request access to their personal data, and to
            correct or delete that data, or to limit its processing. In addition, the data subject has the
            right to data portability, as well as the right to lodge a complaint with a supervisory
            authority. When the data processing is based on consent, the data subject has the right to
            withdraw that consent at any time. The data subject may also exercise all other rights pursuant
            to current data protection regulations (art. 15 et seq. GDPR) by writing to the e-mail:
            <a :href="`mailto:${this.privacyEmail}?subject=${this.privacyEmailSubject}`">{{
                this.privacyEmail
              }}</a>.
          </p>
        </div>
      </div>
    </div>
  </main>

  <!-- Pixel -->
  <Pixel :prop-source="constants.ROUTING.PRIVACY" v-if="flagUsePixel()===true"/>
</template>